import React, { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import styles from '../pages/TutorialLearning.module.css';

export default function TutorialAccessGuard({ programIds, children }) {
  const { currentUser, loading } = useAuth();
  const uid = currentUser?.uid;
  const [verification, setVerification] = useState(null);
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    if (loading || !uid) return;
    let active = true;
    const unsubscribe = onSnapshot(
      doc(db, 'users', uid),
      { includeMetadataChanges: true },
      snapshot => {
        if (!active) return;
        // Cached or optimistic enrollment must not grant access.
        if (snapshot.metadata.fromCache || snapshot.metadata.hasPendingWrites) {
          setVerification(null);
          return;
        }
        const programs = snapshot.data()?.enrolledPrograms;
        setVerification({ uid, programs: Array.isArray(programs) ? programs : [] });
      },
      error => {
        if (!active) return;
        console.error('[TutorialAccessGuard] Enrollment verification failed:', error);
        setVerification({ uid, error: true });
      }
    );
    return () => { active = false; unsubscribe(); };
  }, [uid, loading, retry]);

  if (!loading && !currentUser) return <Navigate replace to="/tutorials" />;

  const verified = verification?.uid === uid ? verification : null;
  if (verified?.error) {
    return (
      <main className={styles.wrapper}>
        <div className={styles.emptyState} role="alert">
          <h1>Unable to verify your enrollment</h1>
          <p>Please check your connection and try again.</p>
          <button type="button" onClick={() => { setVerification(null); setRetry(value => value + 1); }}>Try again</button>
          <p><Link to="/tutorials">Back to tutorials</Link></p>
        </div>
      </main>
    );
  }
  if (loading || !verified) {
    return <main className={styles.wrapper}><p role="status">Verifying your enrollment. Please stay connected...</p></main>;
  }
  const programId = programIds.find(id => verified.programs.includes(id));
  if (!programId) return <Navigate replace to="/tutorials" />;
  return typeof children === 'function' ? children(programId) : children;
}
